// Libraries
import { Button, Flex } from '@chakra-ui/react';
import { useRef, useState } from 'react';
// Components
import { DatePicker } from '@organisms/index';
import { FormSubmit, ResetIcon } from '@atoms/index';
// Utils
import text from '@translations/en.json';
// Types
import { DatePickerResetHandle, RangePickerProps } from './RangePicker.types';

/**
 * RangePicker component represents a date range picker.
 *
 * @component
 * @param {RangePickerProps} props - The component props.
 * @param {RangeDate} props.selectedDate - The initial state of the date range picker.
 * @param {number[]} props.availableYears - The available years in the date range picker.
 * @param {Function} props.onSelect - The function to handle the selected date range.
 * @returns {ReactElement} RangePicker component.
 */
export const RangePicker = ({
  selectedDate,
  availableYears,
  onSelect,
  defaultDate,
  limits,
  initialDate,
}: RangePickerProps) => {
  const [toDate, setToDate] = useState(selectedDate.to);
  const [fromDate, setFromDate] = useState(selectedDate.from);

  const fromDatePickerRef = useRef<DatePickerResetHandle>(null);
  const toDatePickerRef = useRef<DatePickerResetHandle>(null);
  const { minDate = undefined, maxDate = undefined } = limits ?? {};

  /**
   * Handles the selection of the "from" date.
   */
  const onSelectedFromDate = (date: Date[]) => setFromDate(date);

  /**
   * Handles the selection of the "to" date.
   */
  const onSelectedToDate = (date: Date[]) => setToDate(date);

  /**
   * Applies the selected dates and triggers the `onSelect` callback if provided.
   */
  const applyDates = () => {
    const dates = { from: fromDate, to: toDate };

    if (onSelect) {
      onSelect(dates);
    }
  };

  /**
   * Clears the selected dates and resets the date pickers.
   */
  const clearMethod = () => {
    if (fromDatePickerRef.current && toDatePickerRef.current && onSelect) {
      fromDatePickerRef.current.resetDate();
      toDatePickerRef.current.resetDate();
    }
  };

  const togglePickerFrom = () => {
    fromDatePickerRef.current && fromDatePickerRef.current.togglePicker();
    toDatePickerRef.current && toDatePickerRef.current.togglePicker(false);
  };
  const togglePickerTo = () => {
    toDatePickerRef.current && toDatePickerRef.current.togglePicker();
    fromDatePickerRef.current && fromDatePickerRef.current.togglePicker(false);
  };

  return (
    <Flex flexDirection='column'>
      <DatePicker
        onClick={togglePickerFrom}
        label={text.from}
        selected={fromDate}
        onSelected={onSelectedFromDate}
        availableYears={availableYears}
        ref={fromDatePickerRef}
        shortcutDate={defaultDate.from[0]}
        shortcutDateLabel={text.shortcut_label_from}
        minDate={minDate}
        maxDate={toDate[0]}
        initialDate={initialDate.from[0]}
      />
      <DatePicker
        label={text.to}
        selected={toDate}
        onSelected={onSelectedToDate}
        availableYears={availableYears}
        ref={toDatePickerRef}
        shortcutDate={defaultDate.to[0]}
        shortcutDateLabel={text.shortcut_label_to}
        minDate={fromDate[0]}
        maxDate={maxDate}
        onClick={togglePickerTo}
        initialDate={initialDate.to[0]}
      />
      <Flex justifyContent='space-between' p={4}>
        <Button variant='baseLight' fontSize='m' fontWeight='medium' onClick={clearMethod} color='tealishBlue'>
          <ResetIcon boxSize={2.5} mr={1} color='tealishBlue' />
          {text.clear}
        </Button>
        <FormSubmit onClick={applyDates}>Apply</FormSubmit>
      </Flex>
    </Flex>
  );
};
